import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  TextField,
  IconButton,
  Stack,
  Button,
  Typography,
  keyframes,
  Fade,
  Tooltip,
  Divider,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

import Checkbox from "@mui/material/Checkbox";
import ReactMarkdown from "react-markdown";
import { useParams } from "react-router-dom";
import LaunchIcon from "@mui/icons-material/Launch";
import HistoryIcon from "@mui/icons-material/History";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import "./ChatBot.css";
import CallMadeIcon from "@mui/icons-material/CallMade";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import rehypeRaw from "rehype-raw";
import { askChatbot } from "../../services/chatBotService";
const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const ChatBot = () => {
  const [input, setInput] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [response, setResponse] = useState(null);
  const [userMessage, setUserMessage] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [sendMessage, setSendMessage] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [selectedCharts, setSelectedCharts] = useState([]);
  const [conversationId, setConversationId] = useState("");
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [initialQuestions, setInitialQuestions] = useState([]);
  const { appName } = useParams();
  const { t } = useTranslation();
  const chatboxRef = useRef(null);
  // Reset UUID in local storage on page refresh

  useEffect(() => {
    if (sendMessage) {
      handleSend(input);
      setSendMessage(false);
    }
  }, [sendMessage]);

  const handleSend = async (input_question) => {
    if (input_question) {
      setInput("");
      setConversationHistory((prev) => [
        ...prev,
        {
          user: input_question,
          response: null,
          timestamp: new Date().toISOString(), // Timestamp when user asks the question
        },
      ]);

      setIsAnalyzing(true);
      setResponse(null);
      setUserMessage(input_question);
      setShowSuggestions(false);

      try {
        const response = await askChatbot({
          question: input_question,
        });
        console.log("In chatbot askchatbot response", response);
        setConversationHistory((prev) => {
          const newHistory = [...prev];
          newHistory[newHistory.length - 1] = {
            ...newHistory[newHistory.length - 1],
            response: response.data.answer,
            timestamp: new Date().toISOString(), // Timestamp when response is received
          };
          return newHistory;
        });
      } catch (error) {
        const errorResponse = {
          answer: t('UI_TEXT.SORRY_THERE_WAS_AN_ERROR_PROCESSING_YOUR_REQUEST'),
          chart_type: "LineChart",
          data: [],
          x_axis_keys: [],
          y_axis_keys: [],
        };

        setConversationHistory((prev) => {
          const newHistory = [...prev];
          newHistory[newHistory.length - 1] = {
            ...newHistory[newHistory.length - 1],
            response: errorResponse,
            timestamp: new Date().toISOString(), // Timestamp for error response
          };
          return newHistory;
        });

        setResponse(errorResponse);
      } finally {
        setIsAnalyzing(false);
      }
    }
  };

  const smoothScrollToBottom = () => {
    const element = chatboxRef.current;
    if (!element) return;

    const startPosition = element.scrollTop;
    const endPosition = element.scrollHeight - element.clientHeight;
    const distance = endPosition - startPosition;
    const duration = 1000;
    const startTime = performance.now();

    const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutQuad(progress);

      element.scrollTop = startPosition + distance * easedProgress;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  useEffect(() => {
    if (chatboxRef.current) {
      smoothScrollToBottom();
    }
  }, [conversationHistory]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      setInput(input.trim());
      setSendMessage(true);
    }
  };

  const handleSuggestedQuestion = (question) => {
    setInput(question);
    setSendMessage(true);
  };

  const handleClearResults = async () => {
    try {
      setResponse(null);
      setUserMessage("");
      setShowSuggestions(true);
      setConversationHistory([]);
      // const initial_questions = getInitialQuestions(appName);
      // setInitialQuestions(initial_questions[0]?.questions || []);
    } catch (error) {
      console.error("Error clearing conversation:", error);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
      height="100%"
      overflow={"hidden"}
      mb={2}
      position="relative"
    >
      <Box
        sx={{
          width: "100%",
          padding: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          height: "fit-content",
          background: "#fff",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#181818" }}>
          {t('CHAT_SUPPORT.CHAT_WITH_US')}
        </Typography>
        <Box>
          <Tooltip title={t("UI_TEXT.TOOLTIP_START_NEW_CHAT")} arrow>
            <IconButton
              size="small"
              onClick={handleClearResults}
              color="primary"
            >
              <AddCircleIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexDirection: "column",
          padding: 2,
          width: "100%",
        }}
      >
        {showSuggestions && (
          <Box
            sx={{
              maxWidth: "100%",
              width: "100%",
              height: "fit-content",
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              rowGap: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "10px",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                pl: 1,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 50, color: "#aaa" }} />
              <Typography
                variant="h3"
                sx={{ color: "#181818", fontSize: "20px", fontWeight: "500" }}
              >
                {t('CHAT_SUPPORT.HOW_CAN_I_HELP_YOU_TODAY')}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "5px",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                pl: 1,
              }}
            >
              {initialQuestions.length > 0 ? (
                initialQuestions.map((quest) => (
                  <Typography
                    className="quick-question"
                    variant="body1"
                    sx={{ color: "primary.light" }}
                    onClick={() => handleSuggestedQuestion(quest)}
                  >
                    {quest}
                  </Typography>
                ))
              ) : (
                <Typography
                  className="quick-question"
                  variant="body1"
                  sx={{ color: "primary.light" }}
                >
                  {t('CHAT_SUPPORT.ASK_ME_ANYTHING_RELATED_TO_THE_PRODUCT')}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Chat Area with scrollable conversation history */}
        {conversationHistory.length > 0 && (
          <Box
            sx={{
              maxWidth: "100%",
              margin: "0 auto",
              width: "100%",
              flex: 1,
              overflowY: "auto",
              height: "80%",
              maxHeight: "80vh",
              padding: "10px",
            }}
            ref={chatboxRef}
          >
            <Box sx={{ mt: 4 }}>
              {/* Conversation History */}
              {conversationHistory.map((item, index) => (
                <Box key={index} sx={{ mb: 4 }}>
                  {/* User Message */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Box
                      sx={{
                        bgcolor: "primary.main",
                        color: "white",
                        p: 2,
                        borderRadius: "20px 20px 0 20px",
                        maxWidth: "70%",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                        position: "relative",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {item.user}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Timestamp */}
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        fontStyle: "italic",
                        mt: 1,
                        mr: 1,
                      }}
                    >
                      {new Date(item.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>

                  {/* Response or Analyzing State */}
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        mb: 2,
                        mt: 1,
                      }}
                    >
                      {item.response === null ? (
                        <Typography
                          variant="body1"
                          sx={{
                            animation: `${pulse} 1.5s ease-in-out infinite`,
                          }}
                        >
                          {t('CHAT_SUPPORT.ANALYZING')}
                        </Typography>
                      ) : item.response ? (
                        <Box>
                          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                            {item.response}
                          </ReactMarkdown>
                        </Box>
                      ) : (
                        <Typography variant="body1" color="error">
                          {t('ERRORS.ERROR_GENERATING_RESPONSE_PLEASE_TRY_AGAIN')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            zIndex: 10,
            // padding: "20px",
            // background:
            // 	"linear-gradient(to bottom, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.95) 40%, #ffffff 100%)",
            // backdropFilter: "blur(10px)",
            // border: "1px solid rgba(0, 0, 0, 0.1)",
            // boxShadow: "0px -4px 20px rgba(0, 0, 0, 0.05)",
            // borderRadius: "20px",
          }}
        >
          <Stack direction="column" spacing={1.5}>
            <TextField
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('CHAT_SUPPORT.ASK_ME_ANYTHING_1')}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => {
                      setInput(input.trim());
                      setSendMessage(true);
                    }}
                    disabled={!input.trim()}
                    color="primary"
                    sx={{
                      bgcolor: "primary.main",
                      color: "white",
                      borderRadius: "50%",
                      padding: "8px",
                      "&:hover": {
                        bgcolor: "primary.dark",
                        transform: "scale(1.05)",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "grey.300",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <SendIcon sx={{ fontSize: "18px" }} />
                  </IconButton>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "24px", // MORE ROUNDED FOR CHAT STYLE
                  backgroundColor: "white",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  height: "48px", // FIXED PROFESSIONAL HEIGHT
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  },
                  "&.Mui-focused": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "12px 16px",
                  fontSize: "14px",
                },
                width: "100%",
              }}
            />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatBot;
